import type { ServiceAssignment } from '../../types';

/**
 * Projekt-Kickoff: gemeinsame Typen für Termin-Logik, Mail, Datenzugriff und
 * Oberfläche. Bauplan: docs/projekt-kickoff-plan.md.
 *
 * Diese Datei ist der Vertrag zwischen den Modulen — Änderungen hier treffen
 * projectKickoff.ts, kickoffMailer.ts, kickoffService.ts und den Dialog.
 */

/** 'alle' = eine Person für Planung UND Ausführung (Normalfall). */
export type KickoffBereich = 'alle' | 'planung' | 'ausfuehrung';

export type KickoffGrund = 'neu' | 'besitzerwechsel' | 'terminaenderung';

export type KickoffTerminArt = 'vorank' | 'sigeplan' | 'begehung' | 'einweisung';

export type KickoffHinweisArt =
    | 'baustart_fehlt'
    | 'frist_ueberschritten'
    | 'turnus_manuell'
    | 'ueberschneidung'
    /**
     * SiGe-Plan im LV, aber kein Samstagsplatz vom Server (Vergabe gescheitert
     * oder alle Plätze belegt). Die Serie fehlt dann in Mail und ICS — eine
     * geratene Uhrzeit kollidierte im Kalender (Codex-Review 11.10.).
     */
    | 'sigeplan_ohne_platz';

export interface KickoffHinweis {
    art: KickoffHinweisArt;
    /** Fertiger deutscher Satz für Mail und Popup. */
    text: string;
}

/** Ein Kalendereintrag (Einzeltermin oder Serie) für Outlook. */
export interface KickoffTermin {
    art: KickoffTerminArt;
    /**
     * Stabile UID — ein erneuter Import aktualisiert statt zu verdoppeln.
     * Begehung: `<projectId>@sigeko-app` (dieselbe wie der Kalender-Download
     * im Hub). Sonst `<projectId>-vorank@…`, `-sigeplan@…`, `-einweisung@…`.
     * Die Vertretungs-UIDs (`-sub-…`) bleiben davon unberührt.
     */
    uid: string;
    /** Betreff im Kalender, z. B. „6251 Begehung – Neubau Kita“. */
    titel: string;
    /** Erster Termin, Ortszeit Berlin, `YYYY-MM-DD`. */
    datum: string;
    /** `HH:MM`, Ortszeit Berlin. */
    uhrzeit: string;
    dauerMin: number;
    /** Ganztägiger Merker ohne Uhrzeit (Begehung ohne feste Wochentage, `wochenMerker`). */
    ganztags?: boolean;
    /** RFC-5545-Regel OHNE DTSTART, z. B. `FREQ=WEEKLY;BYDAY=SA`. Fehlt = Einzeltermin. Nie UNTIL/COUNT (Serien ohne Ende, Entscheid 11.10.). */
    rrule?: string;
    /** Beschreibungszeilen (Projekt, Adresse, Bauherr, Beteiligte …). */
    beschreibung: string[];
    ort?: string;
    /** Kurztext für Mail und Popup: „Do. 05.11.2026“, „jeden Sa 08:30“, „jeden Di + Do 08:00“. */
    anzeige: string;
    hinweise: KickoffHinweis[];
}

/** Alles, was Mail, ICS und Popup über EIN Projekt für EINEN Empfänger brauchen. */
export interface KickoffPlan {
    projectId: string;
    projectNumber: string;
    projectName: string;
    projectAddress: string;
    /** `YYYY-MM-DD` oder null, wenn kein Baustart gepflegt ist. */
    baustart: string | null;
    bauende: string | null;
    bauherr: string;
    auftraggeber: string;
    bereich: KickoffBereich;
    termine: KickoffTermin[];
    /** Projektbezogene Hinweise (Baustart fehlt, Turnus manuell …). */
    hinweise: KickoffHinweis[];
}

/** Zeile aus `public.project_kickoff_acks` (SQL Nr. 146/147). */
export interface KickoffAck {
    id: string;
    project_id: string;
    recipient_id: string;
    bereich: KickoffBereich;
    grund: KickoffGrund;
    created_at: string;
    created_by: string | null;
    mail_gesendet_am: string | null;
    mail_fehler: string | null;
    spaeter_anzahl: number;
    bestaetigt_am: string | null;
    bestaetigt_von: string | null;
    eskaliert_am: string | null;
    /** Halbstunden-Platz der Samstagsserie: 0 = 08:00, 1 = 08:30 … 31 = 23:30. */
    sa_platz: number | null;
}

/** Dreimal „Später“, danach sperrt das Popup (Entscheid 11.10.). */
export const KICKOFF_MAX_SPAETER = 3;

/** Erster Samstagsplatz 08:00, je Platz 30 Minuten später. */
export const KICKOFF_SA_START = '08:00';
export const KICKOFF_SA_DAUER_MIN = 30;
/** Letzter Platz: 08:00 + 31 × 30 min = 23:30. SQL (Nr. 146/147) begrenzt gleich. */
export const KICKOFF_SA_MAX_PLATZ = 31;

/** LV-Zuordnung → Bereich. Nicht aufgeführte Zuordnungen erzeugen keine Termine. */
export const BEREICH_ZUORDNUNGEN: Record<'planung' | 'ausfuehrung', ReadonlyArray<ServiceAssignment>> = {
    planung: ['vorank', 'unterlage', 'sigeplan', 'asplan', 'planning_general'],
    ausfuehrung: ['inspection', 'einweisung', 'site_rules'],
};
